/* The framed field, from the approved auth mockup's `.f` / `.f-lb` / `.f-in` / `.f-msg` rules.
 * `:hover` and `:focus-visible` are additions — the mockup is a static specimen and could not
 * show either (docs/design/README.md). */

.field {
  margin-bottom: var(--space-4);
}

/* `inline`: the frame alone, in a row of a card — the label is off-screen and the row owns the
 * rhythm around it. */
.fieldInline {
  flex: 1;
  min-width: 0;
}

.label {
  display: flex;
  align-items: baseline;
  gap: var(--space-1);
  margin-bottom: var(--space-2);
  font-family: var(--font-body);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  color: var(--text-2);
}

/* The mockup's `.f-lb .opt` — "· opcjonalnie". `--text-2`, never `--text-3`, which fails AA on
 * every light surface (docs/design/README.md). Opacity, not a lighter ink, keeps it subordinate. */
.labelNote {
  font-size: var(--fs-eyebrow);
  font-weight: var(--fw-semibold);
  opacity: 0.8;
}

.frame {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--h-field);
  padding: var(--space-0) var(--space-4);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--r-field);
  background: var(--bg-inset);
  transition-property: border-color, box-shadow;
  transition-duration: var(--dur-fast);
  transition-timing-function: var(--ease);
}

.frame:hover {
  border-color: var(--border-2);
}

/* The whole frame carries the focus ring, not the bare `<input>` — the frame is what reads as the
 * control. A 1px hairline ring, never a blur: no glow, in any state. */
.frame:focus-within {
  border-color: var(--text);
  box-shadow: var(--ring-field);
}

/* The mockup's `.field .frame.ro` — a value that is shown and cannot be edited. Recessed ground,
 * secondary ink, so it reads as a fact rather than as a field somebody forgot to enable.
 *
 * `.frame.frameReadOnly`, at 0-2-0, so it stands against `.frame:hover`'s own 0-2-0 later in this
 * same file — order settles it here rather than chunk order settling it between two files. The
 * focus ring is untouched: the input is still focusable and still has to show that it is. */
.frame.frameReadOnly {
  background: var(--bg-sunken);
}

.frame.frameReadOnly:hover {
  border-color: var(--border);
}

/* `.frame.frameInvalid`, not `.frameInvalid` alone: a bare class loses to `.frame:focus-within`,
 * whose pseudo-class outweighs it, and the error would silently disappear the moment the field took
 * focus. Verified in the browser, not assumed. */
.frame.frameInvalid {
  border-color: var(--danger);
  box-shadow: var(--ring-field-error);
}

/* Invalid AND focused shows both: the border stays red because the problem is still there, and the
 * ring turns to ink because focus must be visible in every state (WCAG 2.4.7). Letting the error win
 * outright would leave a keyboard user with no focus indicator at all on the one field they most need
 * to find. */
.frame.frameInvalid:focus-within {
  border-color: var(--danger);
  box-shadow: var(--ring-field);
}

.icon {
  flex: none;
  width: var(--icon-sm);
  height: var(--icon-sm);
  stroke-width: 1.9;
  color: var(--text-2);
  opacity: 0.75;
}

/* Reset the browser's chrome away: the frame above is the control, and the input inside it is only
 * the text. The outline goes because the frame's `:focus-within` ring replaces it — never because
 * focus should be invisible. */
.input {
  flex: 1;
  min-width: 0;
  padding: var(--space-0);
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--font-body);
  font-size: var(--fs-field);
  font-weight: var(--fw-medium);
  color: var(--text);
}

.input::placeholder {
  color: var(--text-2);
  opacity: 0.7;
}

.input:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

/* Declared AFTER `.input` so the two read in specificity order — a read-only value is shown in the
 * secondary ink, which is what stops it reading as a field somebody forgot to fill in. */
.frameReadOnly .input {
  color: var(--text-2);
  cursor: default;
}

/* A date input paints its own picker button. It cannot be replaced, only toned to sit with the
 * field's own glyph — `color-scheme` on the theme class (tokens/index.css) is what makes it render
 * dark rather than as a white square on the night canvas. */
.input::-webkit-calendar-picker-indicator {
  cursor: pointer;
  opacity: 0.6;
}

.input::-webkit-calendar-picker-indicator:hover {
  opacity: 1;
}

/* The reveal toggle. A real button: it is focusable, it announces its pressed state, and it gets
 * the accent focus ring the mockup could not draw. */
.trailingButton {
  display: flex;
  flex: none;
  padding: var(--space-0);
  border: none;
  background: transparent;
  color: var(--text-2);
  opacity: 0.75;
  transition-property: opacity, color;
  transition-duration: var(--dur-fast);
  transition-timing-function: var(--ease);
}

.trailingButton:hover {
  color: var(--text);
  opacity: 1;
}

.trailingButton:focus-visible {
  border-radius: var(--radius-sm);
  outline: var(--border-thick) solid var(--accent);
  outline-offset: var(--space-1);
}

.trailingIcon {
  width: var(--icon-sm);
  height: var(--icon-sm);
  stroke-width: 1.9;
}

.message {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  margin-top: var(--space-1);
  font-family: var(--font-body);
  font-size: var(--fs-micro);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-normal);

  /* `--danger-text-strong`, not `--danger-ink`: this message sits directly on the warm canvas, where
   * the lighter ink measures 3.69:1 — under AA for 11.5px text. The strong ink measures 5.57:1. */
  color: var(--danger-text-strong);
}

/* The mockup's `.f-msg.good` — "Hasła są zgodne". Same row, confirming ink. */
.messageGood {
  color: var(--good-ink);
}

/* The glyph inside a message: the check beside "passwords match", sized to the 11.5px line. */
.messageIcon {
  flex: none;
  width: var(--space-3);
  height: var(--space-3);
  stroke-width: 3;
}

/* The mockup's `.f-in.ok .tick` — the confirming mark inside the frame, right of the value. It is
 * decorative: the message under the field says the same thing in words, which is what stops this
 * being meaning carried by colour alone. */
.trailingTick {
  flex: none;
  width: var(--icon-sm);
  height: var(--icon-sm);
  stroke-width: 3;
  color: var(--good-ink);
}
